import { X } from "@phosphor-icons/react"
import { AnimatePresence, motion } from "motion/react"
import { IconButton } from "../reusable/Button"
import { cn } from "~/utils/helpers"
import { formatNumber } from "~/utils/formatting"
import { Separator } from "~/components/reusable/Separator"

type CommandbarProps = {
  count: number
  onClear?: () => void
  selectedText?: string
  children?: React.ReactNode
  /** When true, suppresses the leading count badge + selected-text wrapper. */
  hideHeader?: boolean
  className?: string
  ariaLabel?: string
  clearButtonAriaLabel?: string
  inline?: boolean
  /** Sit just above another fixed Commandbar, so two bars can show at once without overlapping. */
  raised?: boolean
}

/**
 * Floating action bar shown when rows are selected — count, bulk actions, clear-selection.
 * In production it's fixed at the bottom of the viewport.
 * Canonical intent: {@link ../design-system/intents.ts COMMAND_BAR_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export default function Commandbar({
  count,
  onClear,
  selectedText,
  children,
  hideHeader = false,
  className = "",
  ariaLabel = "Command bar",
  clearButtonAriaLabel = "Clear selection",
  inline = false,
  raised = false,
}: CommandbarProps) {
  return (
    <AnimatePresence>
      {count > 0 && (
        <div
          className={cn(
            "mx-auto flex w-fit justify-center",
            !inline && "fixed inset-x-0 z-50 pb-12",
            !inline && (raised ? "bottom-14" : "bottom-0"),
          )}
          role="toolbar"
          aria-label={ariaLabel}
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.13, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex flex-row items-center rounded-full bg-base pl-4 pr-2 py-2 shadow-modal gap-2"
          >
            {!hideHeader && (
              <>
                <div className="label-base">
                  {formatNumber(count)} {selectedText ?? "variants selected"}
                </div>

                <Separator orientation="vertical" className="h-3 mx-1" />
              </>
            )}

            <div className={cn("flex flex-row items-center gap-2", className)}>{children}</div>

            {onClear && (
              <IconButton
                variant="transparent"
                size="xs"
                onClick={onClear}
                aria-label={clearButtonAriaLabel}
              >
                <X />
              </IconButton>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
